import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Button, Col, Space, Breadcrumb, Form } from 'antd'
import {
  getHeaders,
  errorNotification,
  successNotification,
  adminUsersUrl,
  carOrdersUrl,
  carsUrl,
  shippingCompaniesUrl,
  postRequest,
  getRequest
} from '../../config'
import NumberInput from '../../components/Forms/NumberInput'
import SelectInput from '../../components/Forms/SelectInput'
import SelectSearch from '../../components/Forms/SelectInputInteractive'
import BasicInput from '../../components/Forms/BasicInput'
import WithData from 'hocs/WithData'
import { withRouter } from 'react-router-dom'
import PageTitle from 'components/PageTitle'

const OrderForm = props => {
  const [payload, setPayload] = useState({
    car: '',
    user: '',
    quantity: 1,
    useCustomAmount: false,
    pricePerUnit: 0,
    eta: '',
    note: '',
    containerNumber: '',
    billOfLading: '',
    shippingCompany: '',
    orderedOn: ''
  })

  const [serverErrors, setServerErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [userQuery, setUserQuery] = useState('')
  const [shippingCompanies, setShippingCompanies] = useState([])
  const [chassisNumberQuery, setChassisNumberQuery] = useState('')
  const [carsLoading, setCarsLoading] = useState(false)
  const [cars, setCars] = useState([])

  const searchCars = async () => {
    const response = await postRequest(
      `${carsUrl}/search`,
      {
        chassisNumber: chassisNumberQuery
      },
      'cars',
      setCarsLoading
    )
    if (response.success) {
      setCars(response.data.results)
    } else {
      if (response.error.response) {
        setServerErrors(response.error.response.data)
      }
    }
  }

  useEffect(() => {
    if (chassisNumberQuery && chassisNumberQuery.length >= 3) searchCars()
    // eslint-disable-next-line
  }, [chassisNumberQuery])

  const searchUsers = async () => {
    setUsersLoading(true)
    const response = await postRequest(
      `${adminUsersUrl}/search`,
      {
        firstName: userQuery
      },
      'users',
      setUsersLoading
    )
    if (response.success) {
      setUsers(response.data.results)
    } else {
      if (response.error.response) {
        setServerErrors(response.error.response.data)
      }
    }
  }

  useEffect(() => {
    if (userQuery && userQuery.length >= 3) searchUsers()
    // eslint-disable-next-line
  }, [userQuery])

  const handleSelect = stateName => value => {
    setPayload({
      ...payload,
      [stateName]: value
    })
  }

  const handleNumberInput = stateName => value => {
    setPayload({
      ...payload,
      [stateName]: value
    })
  }

  const handleInputChange = stateName => event => {
    setPayload({ ...payload, [stateName]: event.target.value })
  }

  const handleSearchUsers = value => {
    setUserQuery(value)
  }

  const handleSearchCars = value => {
    setChassisNumberQuery(value)
  }

  const handleCreate = event => {
    event.preventDefault()

    setLoading(true)
    axios
      .post(
        carOrdersUrl,
        { ...payload, orderedOn: payload.orderedOn ? payload.orderedOn : null },
        {
          headers: getHeaders()
        }
      )
      .then(res => {
        setLoading(false)
        // eslint-disable-next-line react/prop-types
        props.history.push(`/car-orders/${res.data.id}`)
        successNotification('New Order Added')
      })
      .catch(err => {
        setLoading(false)
        if (err.response) {
          setServerErrors(err.response.data)
        }
        errorNotification('Error adding order, retry.')
      })
  }

  const getShippingCompanies = async () => {
    const response = await getRequest(
      shippingCompaniesUrl,
      'shipping companies',
      setLoading
    )
    if (response.success) {
      setShippingCompanies(
        response.data.results.map(ship => {
          return {
            ...ship,
            label: `${ship.name} - ${ship.origin.country}`,
            value: ship.id
          }
        })
      )
    } else {
      if (response.error.response) {
        setServerErrors(response.error.response.data)
      }
    }
  }

  useEffect(() => {
    getShippingCompanies()

    // eslint-disable-next-line
  }, [])

  return (
    <div className="container">
      <PageTitle title="Car order form" />
      <div className="row">
        <div className="col-md-12">
          <Breadcrumb>
            <Breadcrumb.Item href="/">
              {/* <HomeOutlined /> */}
              <span>Home</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item href="/car-orders">Car Orders</Breadcrumb.Item>
            <Breadcrumb.Item>New Car Order</Breadcrumb.Item>
          </Breadcrumb>
          <br />
          <br />
          <div className="card mb-4rem">
            <div className="card-header">New Car Order</div>
            <div className="card-body">
              <Form className="p-2rem" layout="vertical">
                <div
                  className="row"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <div className="col-md-5">
                    <SelectSearch
                      placeholder="search by car chassis number"
                      label="Choose car"
                      name="car"
                      value={payload.car}
                      onChange={handleSelect('car')}
                      optionList={cars.map(car => {
                        return {
                          value: car.id,
                          label: `${car.year} ${car.make.name} ${car.model.name} ${car.trim} - ${car.chassisNo}`
                        }
                      })}
                      showSearch={true}
                      onSearch={handleSearchCars}
                      searchValue={chassisNumberQuery}
                      required={true}
                      loading={carsLoading}
                    />
                    {serverErrors.car &&
                      serverErrors.car.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  <div className="col-md-5">
                    <SelectSearch
                      placeholder="search by phone number"
                      label="Choose customer"
                      name="customer"
                      value={payload.user}
                      onChange={handleSelect('user')}
                      optionList={users.map(us => {
                        return {
                          value: us.id,
                          label: `${
                            us.firstName ? us.firstName : 'No name'
                          } - ${us.phoneNumber}`
                        }
                      })}
                      showSearch={true}
                      onSearch={handleSearchUsers}
                      searchValue={userQuery}
                      required={true}
                      loading={usersLoading}
                    />
                    {serverErrors.user &&
                      serverErrors.user.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  <div className="col-md-5">
                    <SelectInput
                      placeholder={'select shipping company'}
                      label={'Choose shipping company'}
                      name={'shipping company'}
                      value={payload.shippingCompany}
                      onChange={handleSelect('shippingCompany')}
                      optionList={shippingCompanies}
                      required={true}
                      loading={loading}
                    />
                    {serverErrors.shippingCompany &&
                      serverErrors.shippingCompany.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  <div className="col-md-5">
                    <NumberInput
                      label="Quantity"
                      value={payload.quantity}
                      name={'quantity'}
                      placeholder="quantity"
                      handleChange={handleNumberInput('quantity')}
                      required={true}
                      min={1}
                      max={1000}
                    />
                    {serverErrors.quantity &&
                      serverErrors.quantity.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  <div className="col-md-5">
                    <SelectInput
                      placeholder={'select option'}
                      label={
                        'Do you want to use a custom amount for this order?'
                      }
                      name={'useCustomAmount'}
                      value={payload.useCustomAmount}
                      onChange={handleSelect('useCustomAmount')}
                      optionList={[
                        {
                          label: 'Yes',
                          value: true
                        },
                        { label: 'No use product amount', value: false }
                      ]}
                      required={true}
                      loading={loading}
                    />
                    {serverErrors.useCustomAmount &&
                      serverErrors.useCustomAmount.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  {payload.useCustomAmount ? (
                    <div className="col-md-5">
                      <BasicInput
                        label={'Enter order amount'}
                        value={payload.pricePerUnit}
                        name={'pricePerUnit'}
                        placeholder="pricePerUnit"
                        handleChange={handleInputChange('pricePerUnit')}
                        required={true}
                      />
                      {serverErrors.pricePerUnit &&
                        serverErrors.pricePerUnit.map((err, i) => (
                          <div className="text-danger" key={i}>
                            {err}
                          </div>
                        ))}
                    </div>
                  ) : null}

                  {payload.useCustomAmount ? (
                    <div className="col-md-5">
                      <BasicInput
                        label={'A note for this order'}
                        value={payload.note}
                        name={'note'}
                        placeholder="note"
                        handleChange={handleInputChange('note')}
                        required={true}
                      />
                      {serverErrors.note &&
                        serverErrors.note.map((err, i) => (
                          <div className="text-danger" key={i}>
                            {err}
                          </div>
                        ))}
                    </div>
                  ) : null}

                  <div className="col-md-5">
                    <BasicInput
                      label={'Order Placed On (YYYY-MM-DD)'}
                      value={payload.orderedOn}
                      name={'orderedOn'}
                      placeholder="ordered on"
                      handleChange={handleInputChange('orderedOn')}
                      required={false}
                    />
                    {serverErrors.orderedOn &&
                      serverErrors.orderedOn.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  <div className="col-md-5">
                    <BasicInput
                      label={'ETA (YYYY-MM-DD)'}
                      value={payload.eta}
                      name={'eta'}
                      placeholder="eta"
                      handleChange={handleInputChange('eta')}
                      required={false}
                    />
                    {serverErrors.eta &&
                      serverErrors.eta.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  <div className="col-md-5">
                    <BasicInput
                      label={'Container Number'}
                      value={payload.containerNumber}
                      name={'containerNumber'}
                      placeholder="container number"
                      handleChange={handleInputChange('containerNumber')}
                      required={false}
                    />
                    {serverErrors.containerNumber &&
                      serverErrors.containerNumber.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>

                  <div className="col-md-5">
                    <BasicInput
                      label={'Bill of Lading Number'}
                      value={payload.billOfLading}
                      name={'billOfLading'}
                      placeholder="bill of lading number"
                      handleChange={handleInputChange('billOfLading')}
                      required={false}
                    />
                    {serverErrors.billOfLading &&
                      serverErrors.billOfLading.map((err, i) => (
                        <div className="text-danger" key={i}>
                          {err}
                        </div>
                      ))}
                  </div>
                  <div className="col-md-10 text-muted small mb-3">
                    For a car shipped in a container, enter its container number. For a RoRo car,
                    enter the bill of lading number. Tracking uses these to offer the customer our
                    clearing service.
                  </div>

                  {serverErrors.nonFieldErrors &&
                    serverErrors.nonFieldErrors.map((err, i) => (
                      <div className="text-danger" key={i}>
                        {err}
                      </div>
                    ))}

                  <Col md={24}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        marginBottom: '20px'
                      }}
                    >
                      <Space>
                        {/* eslint-disable-next-line react/prop-types */}
                        <Button onClick={() => props.history.goBack()}>
                          Go Back
                        </Button>
                        <Button
                          type="primary"
                          onClick={handleCreate}
                          loading={loading}
                        >
                          Submit
                        </Button>
                      </Space>
                    </div>
                  </Col>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WithData(withRouter(OrderForm))
